import {
  ApplicationQuestion,
  CommonQuestionKeys,
  FieldType,
  KCCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import { YesNoOptions } from '@platform-ui/application/utils/index.js';
import { gte, inList } from '../../../ast/utils.js';
import { CREDIT_RANGES, ETHNICITIES, INCOME_LEVELS } from '../options.js';

type ExtractedCommonQuestionKeys =
  | CommonQuestionKeys.EMAIL
  | CommonQuestionKeys.ETHNICITY
  | CommonQuestionKeys.PHONE_NUMBER
  | CommonQuestionKeys.MAILING_ADDRESS
  | CommonQuestionKeys.HOUSEHOLD_INCOME
  | CommonQuestionKeys.HOUSEHOLD_SIZE;

export const QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | KCCustomQuestionKeys]: ApplicationQuestion;
} = {
  [KCCustomQuestionKeys.BUSINESS_OWNER_NAME]: {
    key: KCCustomQuestionKeys.BUSINESS_OWNER_NAME,
    copy: { title: 'Name of Business Owner' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'businessOwnerName.label',
        copy: 'Please include the full name, first, middle, and last.',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Business Owner Name',
        key: KCCustomQuestionKeys.BUSINESS_OWNER_NAME,
      },
    ],
  },
  [CommonQuestionKeys.EMAIL]: {
    key: CommonQuestionKeys.EMAIL,
    copy: { title: 'Email' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Email,
        copy: 'Email',
        key: CommonQuestionKeys.EMAIL,
      },
    ],
  },
  [CommonQuestionKeys.PHONE_NUMBER]: {
    key: CommonQuestionKeys.PHONE_NUMBER,
    copy: { title: 'Phone' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Phone,
        copy: 'Phone',
        key: CommonQuestionKeys.PHONE_NUMBER,
      },
    ],
  },
  [KCCustomQuestionKeys.COMPANY_LEGAL_NAME]: {
    key: KCCustomQuestionKeys.COMPANY_LEGAL_NAME,
    copy: { title: 'Company Legal Name' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Company Legal Name',
        key: KCCustomQuestionKeys.COMPANY_LEGAL_NAME,
      },
    ],
  },
  [KCCustomQuestionKeys.DOING_BUSINESS_AS]: {
    key: KCCustomQuestionKeys.DOING_BUSINESS_AS,
    copy: {
      title: 'Doing Business As',
    },
    fields: [
      {
        type: FieldType.Text,
        key: `${KCCustomQuestionKeys.DOING_BUSINESS_AS}.name`,
        inputType: TextInputType.Text,
        copy: 'Doing Business As',
        validation: { required: false },
      },
    ],
    skippable: true,
  },
  [KCCustomQuestionKeys.TITLE]: {
    key: KCCustomQuestionKeys.TITLE,
    copy: { title: 'Title' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        copy: 'Title',
        key: `${KCCustomQuestionKeys.TITLE}.title`,
      },
    ],
  },
  [CommonQuestionKeys.ETHNICITY]: {
    key: CommonQuestionKeys.ETHNICITY,
    copy: { title: 'Do you identify as any of the following?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Ethnicity',
        key: 'ethnicity.ethnicity',
        props: { multiple: true },
        options: ETHNICITIES,
      },
      {
        type: FieldType.Typography,
        key: 'ethnicity.other.label',
        copy: 'If you identify with an ethnicity not included in the previous question, please list it here.',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'ethnicity.other',
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
        copy: 'Other ethnicity',
      },
    ],
  },
  [KCCustomQuestionKeys.PRODUCTS_SERVICES]: {
    key: KCCustomQuestionKeys.PRODUCTS_SERVICES,
    copy: {
      title: 'What type of products and/or services do your business provide?',
    },
    fields: [
      {
        type: FieldType.Text,
        key: KCCustomQuestionKeys.PRODUCTS_SERVICES,
        copy: 'Describe the products and/or services',
        inputType: TextInputType.Text,
        props: {
          multiline: true,
          rows: 5,
          characterCounter: { max: 1000 },
        },
      },
    ],
  },
  [KCCustomQuestionKeys.EMPLOYEES]: {
    key: KCCustomQuestionKeys.EMPLOYEES,
    copy: { title: 'How many employees does your business have, including yourself?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'employees.fullTime.label',
        copy: 'Full-time employees who receive a paycheck from the business, 30 hours per week or more',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'employees.fullTime.number',
        copy: 'Full-time',
      },
      {
        type: FieldType.Typography,
        key: 'employees.partTime.label',
        copy: 'Less than 30 hours per week',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'employees.partTime.number',
        copy: 'Part-time',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'employees.contractorsOrOther.number',
        copy: 'Contractors or Other',
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'employees.contractorsOrOther.reason',
        copy: 'Please explain',
        dynamicLogic: gte('employees.contractorsOrOther.number', 1),
        props: {
          multiline: true,
          rows: 4,
          characterCounter: { max: 500 },
        },
      },
    ],
  },
  [KCCustomQuestionKeys.BUSINESS_ADDRESS]: {
    key: KCCustomQuestionKeys.BUSINESS_ADDRESS,
    copy: { title: 'Is your business located in KCMO?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'businessInKCMO.yes.address.label',
        copy: 'What is the address of your business?',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Address,
        key: KCCustomQuestionKeys.BUSINESS_ADDRESS,
        copy: 'Business Address',
      },
    ],
  },
  [KCCustomQuestionKeys.BUSINESS_LICENSE]: {
    key: KCCustomQuestionKeys.BUSINESS_LICENSE,
    copy: {
      title: 'To be eligible for this program, you must maintain an active KCMO business license.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'businessLicense.text',
        copy: 'Please upload a copy of your active, 2024 KCMO business license.',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
      },
      {
        type: FieldType.Document,
        key: KCCustomQuestionKeys.BUSINESS_LICENSE,
      },
    ],
  },
  [KCCustomQuestionKeys.TAX_CLEARANCE_LETTER]: {
    key: KCCustomQuestionKeys.TAX_CLEARANCE_LETTER,
    copy: {
      title: 'To be eligible for this program, you must have a KCMO Tax Clearance Letter.',
    },
    fields: [
      {
        type: FieldType.Typography,
        key: 'taxClearanceLetter.text',
        copy: 'Please upload a copy of your KCMO Tax Clearance Letter',
        props: {
          variant: 'largeBody',
          alignment: 'center',
        },
      },
      {
        type: FieldType.Document,
        key: KCCustomQuestionKeys.TAX_CLEARANCE_LETTER,
      },
    ],
  },
  [KCCustomQuestionKeys.YEARS_IN_BUSINESS]: {
    key: KCCustomQuestionKeys.YEARS_IN_BUSINESS,
    copy: { title: 'Years in Business' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Years in Business',
        key: KCCustomQuestionKeys.YEARS_IN_BUSINESS,
      },
    ],
  },
  [KCCustomQuestionKeys.BUSINESS_BANK_ACCOUNT]: {
    key: KCCustomQuestionKeys.BUSINESS_BANK_ACCOUNT,
    copy: { title: 'Do you have a business bank account?' },
    fields: [
      {
        key: 'businessBankAccount.yesOrNo',
        type: FieldType.RadioList,
        options: YesNoOptions,
      },
      {
        type: FieldType.Typography,
        key: 'businessBankAccount.yes.nameOfInstition.label',
        copy: 'What is the name of the financial institution?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'businessBankAccount.yes.nameOfInstition.name',
        copy: 'Name',
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.Typography,
        key: 'businessBankAccount.yes.checkingOrSavings.label',
        copy: 'Do you have a checking and/or savings account with your current bank?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.RadioList,
        options: YesNoOptions,
        key: 'businessBankAccount.yes.checkingOrSavings.yesOrNo',
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.Typography,
        key: 'businessBankAccount.yes.businessCreditCard.label',
        copy: 'Do you have a business credit card?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.RadioList,
        options: YesNoOptions,
        key: 'businessBankAccount.yes.businessCreditCard.yesOrNo',
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.Typography,
        key: 'businessBankAccount.yes.existingLoans.label',
        copy: 'Do you have any existing loans with your banking institution?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
      {
        type: FieldType.RadioList,
        options: YesNoOptions,
        key: 'businessBankAccount.yes.existingLoans.yesOrNo',
        dynamicLogic: inList('businessBankAccount.yesOrNo', 'yes'),
      },
    ],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: { title: 'Are you a KCMO resident?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'kcmoResident.yes.address.label',
        copy: 'What is your residential address?',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.MAILING_ADDRESS,
        copy: 'Residential Address',
      },
    ],
  },
  [CommonQuestionKeys.HOUSEHOLD_SIZE]: {
    key: CommonQuestionKeys.HOUSEHOLD_SIZE,
    copy: { title: 'How many people are in your household?', intro: '(including yourself)' },
    fields: [
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        copy: 'Number of people in household',
        key: CommonQuestionKeys.HOUSEHOLD_SIZE,
      },
    ],
  },
  [KCCustomQuestionKeys.DEPENDENTS]: {
    key: KCCustomQuestionKeys.DEPENDENTS,
    copy: { title: 'Do you have any Dependents?' },
    fields: [
      {
        type: FieldType.Typography,
        key: 'dependents.dependents.label',
        copy: 'Select All that apply',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        type: FieldType.Dropdown,
        copy: 'Dependents',
        key: 'dependents.dependents',
        options: [
          { label: 'Yes, I have dependents under the age of 18', value: 'yesUnder18' },
          { label: 'Yes, I have dependents 18 years of age or older', value: 'yesOver18' },
          { label: 'No, I do not have any dependents', value: 'no' },
        ],
        props: { multiple: true },
      },
      {
        type: FieldType.Typography,
        key: 'dependents.yesOver18.number.label',
        copy: 'How many dependents do you have that are 18 or older?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('dependents.dependents', 'yesOver18'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'dependents.yesOver18.number',
        copy: 'Number of dependents 18 or older',
        dynamicLogic: inList('dependents.dependents', 'yesOver18'),
      },
      {
        type: FieldType.Typography,
        key: 'dependents.yesUnder18.number.label',
        copy: 'How many dependents under the age of 18 do you have?',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: inList('dependents.dependents', 'yesUnder18'),
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Number,
        key: 'dependents.yesUnder18.number',
        copy: 'Number of dependents under',
        dynamicLogic: inList('dependents.dependents', 'yesUnder18'),
      },
    ],
  },
  [CommonQuestionKeys.HOUSEHOLD_INCOME]: {
    key: CommonQuestionKeys.HOUSEHOLD_INCOME,
    copy: { title: 'What is the combined annual income of your household?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Average Annual Income',
        key: 'householdIncome.annualAmount',
        options: INCOME_LEVELS,
      },
    ],
  },
  [KCCustomQuestionKeys.NET_WORTH_EXCEEDS]: {
    key: KCCustomQuestionKeys.NET_WORTH_EXCEEDS,
    copy: { title: 'Does your household have net worth resources that exceed $10,000?' },
    fields: [
      {
        key: 'netWorthLabel',
        type: FieldType.Typography,
        copy: '401k savings, checking account, or anything that can be converted to cash quickly. This does not include your primary residence or any vehicles.',
        props: { variant: 'body', alignment: 'center' },
      },
      {
        key: KCCustomQuestionKeys.NET_WORTH_EXCEEDS,
        type: FieldType.RadioList,
        options: YesNoOptions,
      },
    ],
  },
  [KCCustomQuestionKeys.CREDIT_SCORE]: {
    key: KCCustomQuestionKeys.CREDIT_SCORE,
    copy: { title: 'What is your personal credit score?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Credit score',
        options: CREDIT_RANGES,
        key: 'creditScore.score',
      },
    ],
  },
  [KCCustomQuestionKeys.YEAR_ESTABLISHED]: {
    key: KCCustomQuestionKeys.YEAR_ESTABLISHED,
    copy: {
      title: 'What month and year did you start or formally incorporate your business?',
      intro: "If you can't recall the exact date, feel free to provide an estimate.",
    },
    fields: [
      {
        type: FieldType.Date,
        copy: 'Month and Year',
        key: KCCustomQuestionKeys.YEAR_ESTABLISHED,
        validation: { required: true, allowedRange: 'past' },
        props: {
          helperText: 'Example: 06/26/1996',
        },
      },
    ],
  },
};
